import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BadgeCheck, Building2, Clock3, Contact, Eye, MessageSquare } from "lucide-react";
import { DashboardShell, StatCard } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { SERVICES, type ServiceType } from "@/lib/services";
import { fetchPartnerAnalytics, ANALYTICS_SERVICES } from "@/lib/analytics";
import { ENQUIRY_STATUS_CLASS, ENQUIRY_STATUS_LABEL } from "@/lib/enquiries";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/partner/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics · Landoo Partner" },
      {
        name: "description",
        content:
          "Track listings, enquiries, contact requests and listing views across your Landoo services.",
      },
      { property: "og:title", content: "Analytics · Landoo Partner" },
      {
        property: "og:description",
        content:
          "Track listings, enquiries, contact requests and listing views across your Landoo services.",
      },
    ],
  }),
  component: PartnerAnalyticsPage,
});

const SERVICE_COLOR: Record<ServiceType, string> = {
  sale: "var(--sale)",
  rent: "var(--rent)",
  lease: "var(--lease)",
  parking: "var(--parking)",
};

function PartnerAnalyticsPage() {
  const { user } = useAuth();
  const [selected, setSelected] = useState<ServiceType[]>(ANALYTICS_SERVICES);

  const services = selected.length > 0 ? selected : ANALYTICS_SERVICES;

  const { data, isLoading } = useQuery({
    queryKey: ["partner", "analytics", user?.id, services.join(",")],
    queryFn: () => fetchPartnerAnalytics(user!.id, services),
    enabled: !!user,
  });

  const toggle = (s: ServiceType) =>
    setSelected((prev) => (prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]));

  const serviceChart = useMemo(
    () =>
      (data?.byService ?? []).map((b) => ({
        name: b.label,
        total: b.total,
        fill: SERVICE_COLOR[b.service],
      })),
    [data],
  );

  return (
    <DashboardShell title="Analytics" role="Partner" items={PARTNER_NAV}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-brown-dark">Performance overview</h2>
          <p className="text-xs text-muted-foreground">
            Activity across your Landoo services. No revenue data is shown.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {SERVICES.map((s) => {
            const on = selected.includes(s.value);
            return (
              <button
                key={s.value}
                onClick={() => toggle(s.value)}
                aria-pressed={on}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                  on
                    ? "border-brown bg-brown text-primary-foreground shadow-soft"
                    : "border-border bg-card text-brown-dark hover:bg-brand-soft"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading || !data ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label="Total listings" value={data.totals.listings} icon={Building2} />
            <StatCard
              label="Active listings"
              value={data.totals.active}
              hint="Approved and live"
              icon={BadgeCheck}
            />
            <StatCard
              label="Pending listings"
              value={data.totals.pending}
              hint="Awaiting admin review"
              icon={Clock3}
            />
            <StatCard label="Total enquiries" value={data.totals.enquiries} icon={MessageSquare} />
            <StatCard label="Contact requests" value={data.totals.contactRequests} icon={Contact} />
            <StatCard label="Listing views" value={data.totals.views} icon={Eye} />
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <ChartCard title="Listings by service" subtitle="Total listings per service">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={serviceChart} margin={{ left: -18, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
                  <Tooltip cursor={{ fill: "var(--brand-soft)" }} contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="total" name="Listings" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Monthly enquiries" subtitle="Last 6 months">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.monthlyEnquiries} margin={{ left: -18, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Enquiries"
                    stroke="var(--coral)"
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Monthly views" subtitle="Last 6 months" className="lg:col-span-2">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.monthlyViews} margin={{ left: -18, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={11} />
                  <Tooltip cursor={{ fill: "var(--brand-soft)" }} contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="count" name="Views" fill="var(--brown)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="mt-5 rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-brown-dark">Latest 10 enquiries</h3>
              <Link to="/partner/enquiries" className="text-xs font-semibold text-coral">
                View all
              </Link>
            </div>

            {data.latestEnquiries.length === 0 ? (
              <p className="mt-3 text-xs text-muted-foreground">
                No enquiries yet for the selected services.
              </p>
            ) : (
              <>
                {/* Desktop table */}
                <div className="mt-3 hidden overflow-x-auto md:block">
                  <table className="w-full text-left text-xs">
                    <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="py-2 pr-3 font-semibold">Customer</th>
                        <th className="py-2 pr-3 font-semibold">Listing</th>
                        <th className="py-2 pr-3 font-semibold">Service</th>
                        <th className="py-2 pr-3 font-semibold">Status</th>
                        <th className="py-2 font-semibold">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.latestEnquiries.map((e) => (
                        <tr key={e.id} className="border-t border-border">
                          <td className="py-2.5 pr-3 font-semibold text-brown-dark">
                            {e.customer_name ?? "Customer"}
                          </td>
                          <td className="py-2.5 pr-3 text-muted-foreground">
                            {e.listing_title ?? "—"}
                          </td>
                          <td className="py-2.5 pr-3 capitalize text-muted-foreground">
                            {e.service_type}
                          </td>
                          <td className="py-2.5 pr-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${ENQUIRY_STATUS_CLASS[e.enquiry_status]}`}
                            >
                              {ENQUIRY_STATUS_LABEL[e.enquiry_status]}
                            </span>
                          </td>
                          <td className="py-2.5 text-muted-foreground">
                            {formatDate(e.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <ul className="mt-3 space-y-2 md:hidden">
                  {data.latestEnquiries.map((e) => (
                    <li key={e.id} className="rounded-xl border border-border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-brown-dark">
                          {e.customer_name ?? "Customer"}
                        </span>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${ENQUIRY_STATUS_CLASS[e.enquiry_status]}`}
                        >
                          {ENQUIRY_STATUS_LABEL[e.enquiry_status]}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {e.listing_title ?? "—"}
                      </div>
                      <div className="mt-1 text-[11px] capitalize text-muted-foreground">
                        {e.service_type} · {formatDate(e.created_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}

const TOOLTIP_STYLE = {
  borderRadius: 12,
  border: "1px solid var(--border)",
  background: "var(--card)",
  fontSize: 12,
} as const;

function ChartCard({
  title,
  subtitle,
  className = "",
  children,
}: {
  title: string;
  subtitle?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-4 shadow-soft ${className}`}>
      <h3 className="text-sm font-bold text-brown-dark">{title}</h3>
      {subtitle && <p className="mb-2 text-[11px] text-muted-foreground">{subtitle}</p>}
      {children}
    </div>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
